import {
  clearOutbound,
  heartbeats,
  listOutbound,
  outboundAudio,
  type OutboundEntry,
} from "../system/outbound.js";
import { h } from "./dom.js";

export interface LlmCall {
  at: number;
  provider: string;
  model: string;
  ms: number;
  request: unknown;
  response: unknown;
}

const time = (at: number) =>
  new Date(at).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const pretty = (text: string) => {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

const pre = (text: string) =>
  h(
    "pre",
    {
      style: {
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxHeight: "40vh",
        overflow: "auto",
        fontSize: "0.8rem",
      },
    },
    text,
  );

function entryRow(e: OutboundEntry): HTMLElement {
  const play = h("button", { class: "ghost" }, "▶ odsłuchaj");
  play.onclick = () => {
    void outboundAudio(e.id!).then((blob) => {
      if (!blob) {
        play.textContent = "nagranie usunięte (limit 50)";
        return;
      }
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      const free = () => URL.revokeObjectURL(url);
      audio.onended = free;
      audio.onerror = free;
      void audio.play().catch(free);
    });
  };
  return h(
    "details",
    { style: { borderBottom: "1px solid #3a2f4a", padding: "0.3em 0" } },
    h(
      "summary",
      {},
      `${time(e.at)} · ${e.kind === "ws" ? "WS" : e.method} → ${e.dest} · ${e.bytes} B${e.hasAudio ? " · 🎤" : ""}`,
    ),
    e.hasAudio ? play : "",
    h("div", { class: "pixel", style: { marginTop: "0.4em" } }, "Wysłane:"),
    pre(pretty(e.body) || "(bez treści)"),
    e.response ? h("div", { class: "pixel" }, "Odpowiedź:") : "",
    e.response ? pre(e.response) : "",
  );
}

function llmRow(c: LlmCall): HTMLElement {
  const res = c.response as
    { usage?: { inputTokens: number; outputTokens: number }; error?: string } | undefined;
  const tokens = res?.usage
    ? ` · ${res.usage.inputTokens}/${res.usage.outputTokens} tok.`
    : res?.error
      ? " · błąd"
      : "";
  return h(
    "details",
    { style: { borderBottom: "1px solid #3a2f4a", padding: "0.3em 0" } },
    h("summary", {}, `${time(c.at)} · ${c.provider} ${c.model} · ${c.ms} ms${tokens}`),
    h("div", { class: "pixel", style: { marginTop: "0.4em" } }, "Wysłane do modelu:"),
    pre(JSON.stringify(c.request, null, 2)),
    h("div", { class: "pixel" }, "Odpowiedź modelu:"),
    pre(JSON.stringify(c.response, null, 2)),
  );
}

/** Settings → Co wysłałem: this device's outbound log and the Worker's calls to the model. */
export function outboundView(loadLlm: () => Promise<LlmCall[] | null>): HTMLElement {
  const list = h("div", {});
  const tabDevice = h("button", { class: "primary" }, "To urządzenie");
  const tabLlm = h("button", {}, "Worker → model");
  const clear = h("button", { class: "ghost" }, "Wyczyść dziennik urządzenia");

  const showDevice = async () => {
    tabDevice.className = "primary";
    tabLlm.className = "";
    clear.classList.remove("hidden");
    list.replaceChildren(h("p", {}, "Wczytuję…"));
    const entries = await listOutbound();
    list.replaceChildren(
      h(
        "p",
        { style: { fontSize: "0.85rem" } },
        `Wszystko, co to urządzenie wysłało (ostatnie 7 dni, max 200 wpisów, 50 nagrań). Ping co 25 s nie jest zapisywany: ${heartbeats} od startu. Token nigdy nie jest zapisywany.`,
      ),
      ...(entries.length ? entries.map(entryRow) : [h("p", {}, "Na razie nic.")]),
    );
  };
  const showLlm = async () => {
    tabLlm.className = "primary";
    tabDevice.className = "";
    clear.classList.add("hidden");
    list.replaceChildren(h("p", {}, "Wczytuję z Workera…"));
    const calls = await loadLlm();
    list.replaceChildren(
      h(
        "p",
        { style: { fontSize: "0.85rem" } },
        "Ostatnie 50 wywołań modelu z twojego Workera: dokładny prompt (oba fragmenty systemowe, historia, nazwy narzędzi) i odpowiedź. Tylko tekst, bez audio.",
      ),
      ...(calls === null
        ? [h("p", {}, "Nie udało się pobrać (offline albo tryb demo).")]
        : calls.length
          ? calls.map(llmRow)
          : [h("p", {}, "Na razie nic (albo działa regułowy mock, który nie wysyła niczego na zewnątrz).")]),
    );
  };
  tabDevice.onclick = () => void showDevice();
  tabLlm.onclick = () => void showLlm();
  clear.onclick = () => void clearOutbound().then(showDevice);
  void showDevice();
  return h(
    "div",
    {},
    h(
      "div",
      { style: { display: "flex", gap: "0.6em", flexWrap: "wrap", marginBottom: "0.6em" } },
      tabDevice,
      tabLlm,
      clear,
    ),
    list,
  );
}
